import { useEffect } from 'react'

interface SeoProps {
  title: string
  description: string
  canonical: string
  ogImage?: string
  ogType?: string
  jsonLd?: Record<string, any>[]
  robots?: string
}

export function useSeo({
  title,
  description,
  canonical,
  ogImage = 'https://noorddigital.nl/og-image.jpg',
  ogType = 'website',
  jsonLd = [],
  robots = 'index,follow',
}: SeoProps) {
  useEffect(() => {
    // Title
    document.title = title

    // Helper to upsert meta tags
    const upsertMeta = (
      selector: string,
      attribute: string,
      value: string,
      createFn: () => HTMLMetaElement,
    ) => {
      let el = document.querySelector(selector) as HTMLMetaElement
      if (!el) {
        el = createFn()
        document.head.appendChild(el)
      }
      el.setAttribute(attribute, value)
    }

    // Standard meta
    upsertMeta('meta[name="description"]', 'content', description, () => {
      const el = document.createElement('meta')
      el.name = 'description'
      return el
    })
    upsertMeta('meta[name="robots"]', 'content', robots, () => {
      const el = document.createElement('meta')
      el.name = 'robots'
      return el
    })
    upsertMeta('meta[name="author"]', 'content', 'Noord Digital', () => {
      const el = document.createElement('meta')
      el.name = 'author'
      return el
    })

    // Open Graph
    const ogTags = {
      'og:title': title,
      'og:description': description,
      'og:type': ogType,
      'og:url': canonical,
      'og:site_name': 'Noord Digital',
      'og:locale': 'nl_NL',
      'og:image': ogImage,
    }
    Object.entries(ogTags).forEach(([property, content]) => {
      upsertMeta(`meta[property="${property}"]`, 'content', content, () => {
        const el = document.createElement('meta')
        el.setAttribute('property', property)
        return el
      })
    })

    // Twitter Card
    const twitterTags = {
      'twitter:card': 'summary_large_image',
      'twitter:title': title,
      'twitter:description': description,
      'twitter:image': ogImage,
    }
    Object.entries(twitterTags).forEach(([name, content]) => {
      upsertMeta(`meta[name="${name}"]`, 'content', content, () => {
        const el = document.createElement('meta')
        el.name = name
        return el
      })
    })

    // Canonical
    let canonicalEl = document.querySelector(
      'link[rel="canonical"]',
    ) as HTMLLinkElement
    if (!canonicalEl) {
      canonicalEl = document.createElement('link')
      canonicalEl.rel = 'canonical'
      document.head.appendChild(canonicalEl)
    }
    canonicalEl.href = canonical

    // JSON-LD
    const scriptIds: string[] = []
    jsonLd.forEach((schema, index) => {
      const id = `json-ld-${index}`
      scriptIds.push(id)
      let scriptEl = document.getElementById(id) as HTMLScriptElement
      if (!scriptEl) {
        scriptEl = document.createElement('script')
        scriptEl.id = id
        scriptEl.type = 'application/ld+json'
        document.head.appendChild(scriptEl)
      }
      scriptEl.text = JSON.stringify(schema)
    })

    // Cleanup
    return () => {
      scriptIds.forEach((id) => {
        const el = document.getElementById(id)
        if (el) el.remove()
      })
    }
  }, [
    title,
    description,
    canonical,
    ogImage,
    ogType,
    JSON.stringify(jsonLd),
    robots,
  ])
}
